import type { ReportDocument } from '../report-document';

/**
 * WHAT A SAVED REPORT CONTAINED — FROZEN — M10 / S10-05.
 *
 * **PURE. NO REACT, NO NETWORK, NO LIVE DATA.** This turns the report content
 * model into a historical record that can be read years later without touching
 * today's Project, Captures, photos or Settings.
 *
 * **WHY A SEPARATE SHAPE RATHER THAN STORING `ReportDocument`.** Two reasons,
 * both structural:
 *
 *   1. `ReportPhoto` carries `view: CapturePhotoView`, which drags an entire
 *      `capture_photo` row and the device's staged-photo state along with it.
 *      Persisting that would freeze transport bookkeeping as if it were client
 *      history, and would balloon every row.
 *   2. **The logo bytes are deliberately excluded.** The logo is already
 *      visually frozen inside the stored PDF; copying ~220 KB of base64 into
 *      every saved row would multiply the database by an image nothing reads.
 *      Saved-report detail describes the report; **the PDF is the report.**
 *
 * **PHOTO KEYS ARE KEPT** (Jerry, J-5) so the snapshot can answer *which
 * evidence did this report contain?* without querying current evidence. They
 * are a historical reference, **not permission to re-fetch a photo and present
 * today's version of it as history.**
 *
 * **THE SNAPSHOT IS THE ONLY THING SAVED-REPORT SCREENS READ.** Anything a
 * detail screen wants must be here, because reaching for live data is exactly
 * how history starts drifting.
 */

/** One integer, no framework. Future code needs only to know what it is reading. */
export const SAVED_REPORT_SNAPSHOT_VERSION = 1;

export interface SavedReportBrandingSnapshot {
  /** Whether the report was generated WITH company branding. */
  readonly included: boolean;
  readonly companyName: string;
  readonly email: string | null;
  readonly phone: string | null;
  /** Whether a logo was printed. **The bytes themselves are NOT stored.** */
  readonly hadLogo: boolean;
}

export interface SavedReportCaptureSnapshot {
  readonly captureId: string;
  /** Exactly as PRINTED, not as the Capture reads today. */
  readonly title: string;
  readonly meta: string;
  readonly beforeKeys: readonly string[];
  readonly afterKeys: readonly string[];
}

export interface SavedReportSnapshot {
  readonly version: number;
  readonly projectName: string;
  readonly reportDate: string;
  readonly description: string | null;
  readonly branding: SavedReportBrandingSnapshot | null;
  readonly captures: readonly SavedReportCaptureSnapshot[];
  readonly counts: {
    readonly captures: number;
    readonly before: number;
    readonly after: number;
    readonly photos: number;
  };
}

/**
 * Freeze the document.
 *
 * **EVERY STRING IS COPIED, NOT REFERENCED.** The result shares no object with
 * the input, so a later mutation of the live model — however unlikely — cannot
 * reach inside a snapshot that has already been taken.
 */
export function buildSavedReportSnapshot(document: ReportDocument): SavedReportSnapshot {
  const captures = document.captures.map((capture) => ({
    captureId: capture.captureId,
    title: capture.title,
    meta: capture.meta,
    beforeKeys: capture.before.map((photo) => photo.key),
    afterKeys: capture.after.map((photo) => photo.key),
  }));

  const before = captures.reduce((total, capture) => total + capture.beforeKeys.length, 0);
  const after = captures.reduce((total, capture) => total + capture.afterKeys.length, 0);

  return {
    version: SAVED_REPORT_SNAPSHOT_VERSION,
    projectName: document.projectName,
    reportDate: document.reportDate,
    description: document.description,
    branding: document.branding
      ? {
          included: true,
          companyName: document.branding.companyName,
          email: document.branding.email,
          phone: document.branding.phone,
          // The FACT of a logo, never the logo. See the header.
          hadLogo: document.branding.logo !== null,
        }
      : null,
    captures,
    counts: {
      captures: captures.length,
      before,
      after,
      photos: before + after,
    },
  };
}

/**
 * Read a snapshot back from the database, refusing anything it cannot trust.
 *
 * **A SAVED REPORT MAY OUTLIVE THE CODE THAT WROTE IT.** Returning `null` for
 * an unreadable snapshot is deliberate: the screen then says the record cannot
 * be displayed, which is honest — and the PDF, which is the actual document,
 * is still openable either way.
 */
export function parseSavedReportSnapshot(value: unknown): SavedReportSnapshot | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Partial<SavedReportSnapshot>;
  if (typeof raw.version !== 'number' || raw.version < 1) return null;
  if (typeof raw.projectName !== 'string' || typeof raw.reportDate !== 'string') return null;
  if (!Array.isArray(raw.captures)) return null;
  return raw as SavedReportSnapshot;
}
